import { useState } from 'react';
import { Calculator, TrendingDown } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';

/** Format F CFA avec séparateur d'espace (1 714 F). */
function fcfa(value: number): string {
  return `${Math.round(value).toLocaleString('fr-FR')} F`;
}

type SliderProps = {
  id: string;
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  value: number;
  suffix: string;
  onChange: (v: number) => void;
};

function Slider({ id, label, hint, min, max, step, value, suffix, onChange }: SliderProps) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="font-marketing-display text-[15px] font-bold text-charcoal-800">
          {label}
        </label>
        <span className="font-marketing-mono text-[15px] font-semibold text-charcoal-800">
          {value.toLocaleString('fr-FR')}
          <span className="ml-1 text-[12px] font-medium text-charcoal-500">{suffix}</span>
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 w-full cursor-pointer accent-lime-500"
      />
      <p className="mt-1.5 font-marketing-body text-[12.5px] leading-snug text-charcoal-500">{hint}</p>
    </div>
  );
}

const JOURS_PAR_MOIS = 26;
const ML_PAR_BOUTEILLE = 750;

export default function BarCalculatorV2() {
  const [verres, setVerres] = useState(60);
  const [mlTrop, setMlTrop] = useState(8);
  const [prixBouteille, setPrixBouteille] = useState(8000);

  const coutParMl = prixBouteille / ML_PAR_BOUTEILLE;
  const mlPerduMois = verres * mlTrop * JOURS_PAR_MOIS;
  const perteMois = mlPerduMois * coutParMl;
  const perteAn = perteMois * 12;
  const bouteillesMois = mlPerduMois / ML_PAR_BOUTEILLE;

  return (
    <section className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="max-w-3xl">
          <SectionLabel>Calcule ta perte</SectionLabel>
          <h2 className="mt-3 font-marketing-display text-[clamp(30px,4.4vw,56px)] font-bold leading-[1.02] tracking-[-0.035em] text-charcoal-800">
            Combien te coûte le <span className="underline-lime">surdosage</span>&nbsp;?
          </h2>
          <p className="mt-4 font-marketing-body text-[17px] leading-[1.55] text-charcoal-600">
            Quelques millilitres de trop par verre, et la marge fuit toute l’année. Bouge les
            curseurs avec tes propres chiffres pour estimer ce que le surdosage te coûte vraiment.
          </p>
        </div>

        <div className="mt-12 grid gap-[2px] border-2 border-charcoal-800 bg-charcoal-800 shadow-neobrutal-8 lg:grid-cols-[1.1fr_1fr]">
          {/* Inputs */}
          <div className="flex flex-col gap-7 bg-white p-7 md:p-9">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800">
                <Calculator className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
              </span>
              <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                Tes paramètres
              </span>
            </div>

            <Slider
              id="calc-verres"
              label="Verres servis par jour"
              hint="Cocktails et boissons au verre, tous bars confondus."
              min={10}
              max={300}
              step={5}
              value={verres}
              suffix="verres/j"
              onChange={setVerres}
            />
            <Slider
              id="calc-mltrop"
              label="Surdosage moyen par verre"
              hint="Le « petit coup de main » du barman. 5 à 10 ml est courant."
              min={2}
              max={25}
              step={1}
              value={mlTrop}
              suffix="ml/verre"
              onChange={setMlTrop}
            />
            <Slider
              id="calc-prix"
              label="Prix d’une bouteille (75 cl)"
              hint="Le coût d’achat de l’alcool principal versé au verre."
              min={2000}
              max={25000}
              step={500}
              value={prixBouteille}
              suffix="F / 75 cl"
              onChange={setPrixBouteille}
            />
          </div>

          {/* Result */}
          <div className="flex flex-col justify-between gap-6 bg-charcoal-800 p-7 text-white md:p-9">
            <div className="flex items-center gap-2 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
              <TrendingDown className="h-4 w-4 text-terracotta-400" strokeWidth={2.2} aria-hidden="true" />
              Perte estimée
            </div>

            <div>
              <div className="font-marketing-mono text-[10px] uppercase tracking-[0.1em] text-charcoal-300">
                Par mois
              </div>
              <div className="mt-1 font-marketing-display text-[clamp(36px,6vw,64px)] font-bold leading-[0.95] text-terracotta-400">
                {fcfa(perteMois)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="border-2 border-charcoal-700 bg-charcoal-900 px-4 py-3">
                <div className="font-marketing-mono text-[9px] uppercase tracking-[0.08em] text-charcoal-300">
                  Par an
                </div>
                <div className="mt-1 font-marketing-display text-[20px] font-bold text-white">
                  {fcfa(perteAn)}
                </div>
              </div>
              <div className="border-2 border-charcoal-700 bg-charcoal-900 px-4 py-3">
                <div className="font-marketing-mono text-[9px] uppercase tracking-[0.08em] text-charcoal-300">
                  Bouteilles/mois
                </div>
                <div className="mt-1 font-marketing-display text-[20px] font-bold text-white">
                  ≈ {bouteillesMois.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
                </div>
              </div>
            </div>

            <div className="border-l-2 border-lime-500 bg-charcoal-900 px-4 py-3">
              <p className="font-marketing-body text-[13px] leading-snug text-charcoal-200">
                Velko Bar chiffre cet écart en F CFA, ingrédient par ingrédient, et te dit lequel
                fuit. <span className="font-semibold text-lime-400">Estimation illustrative</span> —
                ajuste-la avec tes vrais chiffres.
              </p>
            </div>

            <NeobrutalButton as="link" to="/register" variant="primary" size="md">
              Récupérer cette marge
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14" />
                <path d="M13 6l6 6-6 6" />
              </svg>
            </NeobrutalButton>
          </div>
        </div>
      </div>
    </section>
  );
}
